'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Moon as MoonIcon, Sun as SunIcon } from '@carbon/icons-react'
import { useTheme } from 'next-themes'
import { useThemeSweep } from '@/components/theme-sweep-provider'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * The header mounts one toggle for the mobile bar and one for the desktop bar,
 * so both are in the DOM at all times. Without a claim, each would bind the "d"
 * shortcut and the two handlers would toggle the theme twice — a no-op. Only
 * the first instance to mount owns the shortcut.
 */
let shortcutClaimed = false

interface ThemeModeToggleProps {
  className?: string
  /**
   * Render a bare <button> instead of the pill-morphing <Button>, for the
   * square full-height cells in the site header.
   */
  unstyled?: boolean
}

export function ThemeModeToggle({
  className,
  unstyled = false,
}: ThemeModeToggleProps) {
  const { resolvedTheme, setTheme } = useTheme()
  const { sweepTheme } = useThemeSweep()
  const [mounted, setMounted] = useState(false)
  const ownsShortcut = useRef(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const toggleTheme = useCallback(() => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark'
    // The flip lands at the sweep's centre, where the curtain is fully
    // opaque. Under reduced motion it flips instantly with no curtain.
    sweepTheme(() => setTheme(next))
  }, [resolvedTheme, setTheme, sweepTheme])

  // Keyboard shortcut: "d" toggles dark mode (ignored while typing).
  useEffect(() => {
    if (!shortcutClaimed) {
      shortcutClaimed = true
      ownsShortcut.current = true
    }

    if (!ownsShortcut.current) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== 'd') return
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return

      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return
      }

      event.preventDefault()
      toggleTheme()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleTheme])

  useEffect(
    () => () => {
      if (ownsShortcut.current) {
        shortcutClaimed = false
        ownsShortcut.current = false
      }
    },
    []
  )

  const isDark = mounted && resolvedTheme === 'dark'

  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme'

  const icons = (
    <>
      <SunIcon className="size-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <MoonIcon className="absolute size-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </>
  )

  if (unstyled) {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={label}
        title="Toggle theme (D)"
        className={cn(
          'group relative flex items-center justify-center transition-colors',
          className
        )}
      >
        {icons}
      </button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label={label}
      title="Toggle theme (D)"
      className={cn('group pointer-events-auto relative', className)}
    >
      {icons}
    </Button>
  )
}
